import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Instagram } from "lucide-react";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const CONTACT_IMAGE = "/assets/about-mission-landscape.jpg";
const INSTAGRAM_URL = "https://www.instagram.com/celestialseedcolombia2026/";

export default function Contact() {
  const { t } = useContent("contact");
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Received. We answer everything within 5 days.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const email = t("general.email", "newearth144@proton.me");

  return (
    <div>
      <PageSEO title={"Contact | Celestial Seed Sanctuary"} description={"Contact Celestial Seed Sanctuary. Reach us at newearth144@proton.me."} />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
        <div className="overline mb-6" data-testid="contact-overline">{t("hero.overline", "Contact")}</div>
        <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="contact-title">{t("hero.title", "We answer every letter — slowly, honestly.")}</h1>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid lg:grid-cols-2 gap-12">
        <form onSubmit={submit} className="bg-surface border border-line rounded-2xl p-8 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="overline">Name</Label><Input required data-testid="contact-name" value={f.name} onChange={e=>setF({...f, name:e.target.value})} /></div>
            <div><Label className="overline">Email</Label><Input type="email" required data-testid="contact-email" value={f.email} onChange={e=>setF({...f, email:e.target.value})} /></div>
          </div>
          <div><Label className="overline">Subject</Label><Input data-testid="contact-subject" value={f.subject} onChange={e=>setF({...f, subject:e.target.value})} /></div>
          <div><Label className="overline">Message</Label><Textarea rows={6} required data-testid="contact-message" value={f.message} onChange={e=>setF({...f, message:e.target.value})} /></div>
          <button type="submit" disabled={busy} data-testid="contact-submit" className="pill pill-primary">{busy ? "Sending..." : "Send"}</button>
        </form>
        <div className="space-y-6">
          <img src={CONTACT_IMAGE} alt="Celestial Seed Sanctuary landscape" className="w-full aspect-[16/9] object-cover rounded-2xl border border-line" data-testid="contact-sanctuary-image" />
          <div className="rounded-2xl border border-line bg-surface p-8">
            <div className="overline mb-3">Reach us</div>
            <div className="space-y-3 text-ink">
              <div className="flex items-center gap-3" data-testid="contact-email-line"><Mail className="h-4 w-4 text-terracotta" strokeWidth={1.5} /><a href={`mailto:${email}`} className="hover:text-terracotta">{email}</a></div>
              <div className="flex items-center gap-3" data-testid="contact-location-line"><MapPin className="h-4 w-4 text-terracotta" strokeWidth={1.5} />{t("general.location", "Celestial Seed Sanctuary, Cundinamarca, Colombia")}</div>
              <div className="flex items-center gap-3" data-testid="contact-instagram-line"><Instagram className="h-4 w-4 text-terracotta" strokeWidth={1.5} /><a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">@celestialseedcolombia2026</a></div>
            </div>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-line"><iframe title="map" src="https://www.openstreetmap.org/export/embed.html?bbox=-74.5,4.3,-73.9,4.9&layer=mapnik&marker=4.5983,-74.2469" className="w-full h-full" /></div>
        </div>
      </section>
    </div>
  );
}
